// REST helpers of the app contract for pairing and unpairing (AD-22, AD-43). The shapes are the JSON Schemas
// http-pairing-*.schema.json and http-unpair-response.schema.json in docs/protocol/app-v1 (types generated); these
// functions check with the generated validators and add the normalisation the API and the app share.
import { validate, type Parsed } from './validate.js';
import type {
  HttpError,
  PairingConfirmRequest,
  PairingConfirmResponse,
  PairingLookupRequest,
  PairingLookupResponse,
} from './generated/types.js';

/** Error body of every REST answer that is not 2xx (schema http-error). */
export type ApiError = HttpError;

export const PAIRING_CODE_LENGTH = 8;

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const fail = <T>(error: string): Parsed<T> => ({ ok: false, error });

/**
 * Normalises a typed pairing code like Crockford base32: upper case, without spaces and dashes, O -> 0, I/L -> 1.
 * Returns null unless it is exactly 8 valid characters.
 */
export function normalizePairingCode(input: string): string | null {
  const s = input.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
  return /^[0-9A-HJKMNP-TV-Z]{8}$/.test(s) ? s : null;
}

/** "7K3QX9TB" -> "7K3Q-X9TB" (display grouping). */
export function formatPairingCode(code: string): string {
  return code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
}

/** True if the text contains control characters (U+0000-U+001F, U+007F). */
export function hasControlChars(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 0x20 || c === 0x7f) return true;
  }
  return false;
}

/** A machine or user name: 1-40 characters, no control characters (NFR-15). */
export function isMachineName(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0 && v.length <= 40 && !hasControlChars(v);
}

/** The code or the token (the token counts if both are sent); the code itself is checked by the API. */
function codeOrToken(v: PairingLookupRequest): PairingLookupRequest {
  return v.token !== undefined ? { token: v.token } : { code: v.code as string };
}

export function parsePairingLookupRequest(raw: unknown): Parsed<PairingLookupRequest> {
  if (!isObj(raw)) return fail('malformed');
  const r = validate('http-pairing-lookup-request', raw);
  return r.ok ? { ok: true, value: codeOrToken(r.value) } : fail('invalid_value');
}

export function parsePairingConfirmRequest(raw: unknown): Parsed<PairingConfirmRequest> {
  if (!isObj(raw)) return fail('malformed');
  const r = validate('http-pairing-confirm-request', raw);
  if (!r.ok) return fail('invalid_value');
  const value: PairingConfirmRequest = { ...codeOrToken(r.value) };
  if (r.value.name !== undefined) value.name = r.value.name.trim();
  if (r.value.continueFrom !== undefined) value.continueFrom = r.value.continueFrom;
  return { ok: true, value };
}

export function parsePairingLookupResponse(raw: unknown): Parsed<PairingLookupResponse> {
  const r = validate('http-pairing-lookup-response', raw);
  return r.ok ? r : fail('malformed');
}

export function parsePairingConfirmResponse(raw: unknown): Parsed<PairingConfirmResponse> {
  const r = validate('http-pairing-confirm-response', raw);
  return r.ok ? r : fail('malformed');
}

/** Reads an error body (schema http-error); anything else counts as internal. */
export function parseApiError(raw: unknown): ApiError {
  const r = validate('http-error', raw);
  if (!r.ok) return { code: 'internal' };
  return {
    code: r.value.code,
    ...(r.value.detail !== undefined ? { detail: r.value.detail.slice(0, 200) } : {}),
    ...(r.value.retryAfter !== undefined ? { retryAfter: r.value.retryAfter } : {}),
  };
}
